/**
 * A function that returns a Promise to wait for an image to be fully loaded.
 * Resolves when the image is successfully loaded or immediately if the image is already complete.
 * Rejects if there is an error in loading the image.
 *
 * @param {HTMLImageElement} img - The image element to wait for.
 * @returns {Promise<void>} A Promise that resolves when the image loads successfully
 * or rejects in case of a loading error.
 */
const waitForImage = img =>
  img.complete && img.naturalWidth > 0
    ? Promise.resolve()
    : new Promise((resolve, reject) => {
      img.addEventListener('load', resolve, { once: true })
      img.addEventListener('error', reject, { once: true })
    })

/**
 * Calculates the downscaled dimensions of an object while maintaining its aspect ratio.
 *
 * The function takes the original width and height of an object and reduces them
 * proportionally to fit within a given maximum size. It ensures that neither dimension
 * is scaled to less than 1 pixel.
 *
 * @param {number} w0 - The original width of the object.
 * @param {number} h0 - The original height of the object.
 * @param {number} maxSize - The maximum allowed size for the longest dimension.
 * @returns {{w: number, h: number}} An object containing the downscaled width (`w`) and height (`h`).
 */
const downscaleSize = (w0, h0, maxSize) => {
  const scale = Math.min(1, maxSize / Math.max(w0, h0))
  return {
    w: Math.max(1, Math.round(w0 * scale)),
    h: Math.max(1, Math.round(h0 * scale))
  }
}

/**
 * Creates a canvas element and returns it along with its 2D rendering context.
 *
 * This function generates a new HTML canvas element with the specified width and height.
 * It also retrieves the 2D rendering context for the created canvas. The context is configured
 * with the `willReadFrequently` option set to true, making it optimized for use cases
 * that involve frequent read operations from the canvas.
 *
 * @param {number} w - The width of the canvas in pixels.
 * @param {number} h - The height of the canvas in pixels.
 * @returns {{canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D}}
 * An object containing the created canvas element and its associated 2D rendering context.
 */
const makeCanvasContext = (w, h) => {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  return { canvas, ctx }
}

/**
 * Retrieves the image data from a given image element after scaling it
 * to the specified width and height.
 *
 * @param {HTMLImageElement} img - The image element to extract data from.
 * @param {number} w - The width to scale the image to.
 * @param {number} h - The height to scale the image to.
 * @returns {Uint8ClampedArray} A one-dimensional array containing the
 * pixel data of the image in RGBA format.
 */
const getImageData = (img, w, h) => {
  const { ctx } = makeCanvasContext(w, h)
  ctx.drawImage(img, 0, 0, w, h)
  return ctx.getImageData(0, 0, w, h).data
}

/**
 * Calculates the average color of the corner pixels in an image, considering their alpha transparency.
 *
 * @param {Uint8ClampedArray} data - The pixel data of the image in RGBA format.
 * @param {number} w - The width of the image in pixels.
 * @param {number} h - The height of the image in pixels.
 * @param {number} alphaThreshold - The minimum alpha value to consider a pixel for averaging.
 * @returns {Object} An object representing the average corner color with properties:
 *                   - `r` (number): The average red component (0-255).
 *                   - `g` (number): The average green component (0-255).
 *                   - `b` (number): The average blue component (0-255).
 *                   If no corner pixels meet the alpha threshold, returns white color { r: 255, g: 255, b: 255 }.
 */
const avgCornerColor = (data, w, h, alphaThreshold) => {
  const points = [
    [0, 0],
    [w - 1, 0],
    [0, h - 1],
    [w - 1, h - 1]
  ]

  let r = 0
  let g = 0
  let b = 0
  let n = 0

  points.forEach(([x, y]) => {
    const i = (y * w + x) * 4
    const a = data[i + 3]
    if (a > alphaThreshold) {
      r += data[i]
      g += data[i + 1]
      b += data[i + 2]
      n += 1
    }
  })

  if (!n) return { r: 255, g: 255, b: 255 }

  return { r: r / n, g: g / n, b: b / n }
}

/**
 * Computes the Euclidean color difference between a pixel's RGB values and a background color.
 *
 * @param {Uint8ClampedArray} data - The array of pixel data, typically obtained from image data.
 * @param {number} i - The index of the red component for the pixel in the `data` array.
 * @param {Object} bg - The background color to compare against.
 * @param {number} bg.r - The red component of the background color (0-255).
 * @param {number} bg.g - The green component of the background color (0-255).
 * @param {number} bg.b - The blue component of the background color (0-255).
 * @returns {number} The Euclidean distance between the pixel's RGB values and the background color.
 */
const colorDiff = (data, i, bg) => {
  const dr = data[i] - bg.r
  const dg = data[i + 1] - bg.g
  const db = data[i + 2] - bg.b
  return Math.sqrt(dr * dr + dg * dg + db * db)
}

/**
 * Calculates the bounding box of visible content in an image by comparing the pixel data
 * against a background color and ignoring transparent or similar-colored pixels.
 *
 * @param {Uint8ClampedArray} data - The pixel data of the image, represented as a flat array
 * containing RGBA values.
 * @param {number} w - The width of the image in pixels.
 * @param {number} h - The height of the image in pixels.
 * @param {Array<number>} bg - The background color represented as an array of RGB values ([R, G, B]).
 * @param {number} diffThreshold - The maximum allowed color difference between a pixel and the background
 * for it to be considered part of the background.
 * @param {number} alphaThreshold - The minimum alpha value a pixel must have to be considered visible.
 * @returns {Object|null} An object describing the bounding box with properties `minX`, `minY`, `maxX`, and `maxY`,
 * or `null` if no visible content was found.
 */
const findContentBoundingBox = (data, w, h, bg, diffThreshold, alphaThreshold) => {
  let minX = w
  let minY = h
  let maxX = -1
  let maxY = -1

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4
      const a = data[i + 3]

      if (a <= alphaThreshold) continue
      if (colorDiff(data, i, bg) <= diffThreshold) continue

      if (x < minX) minX = x
      if (y < minY) minY = y
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
    }
  }

  return maxX < 0 ? null : { minX, minY, maxX, maxY }
}

/**
 * Calculates metrics related to how a bounding box (bbox) fills a specified area.
 *
 * @param {Object} bbox - The bounding box object containing its boundaries.
 * @param {number} bbox.minX - The minimum X-coordinate of the bounding box.
 * @param {number} bbox.minY - The minimum Y-coordinate of the bounding box.
 * @param {number} bbox.maxX - The maximum X-coordinate of the bounding box.
 * @param {number} bbox.maxY - The maximum Y-coordinate of the bounding box.
 * @param {number} w - The width of the area within which the bounding box exists.
 * @param {number} h - The height of the area within which the bounding box exists.
 * @param {number} edgeMargin - The margin defining proximity to the edges of the area.
 * @returns {Object} Metrics about the bounding box in relation to the specified area.
 * @returns {number} fillRatio - The ratio of the bounding box's area to the total area.
 * @returns {boolean} edgeTouch - Whether the bounding box touches the edges of the area based on the specified edge margin.
 */
const calcFillMetrics = (bbox, w, h, edgeMargin) => {
  const boxW = bbox.maxX - bbox.minX + 1
  const boxH = bbox.maxY - bbox.minY + 1

  const fillRatio = (boxW * boxH) / (w * h)

  const edgeTouch =
    bbox.minX <= edgeMargin ||
    bbox.minY <= edgeMargin ||
    bbox.maxX >= w - 1 - edgeMargin ||
    bbox.maxY >= h - 1 - edgeMargin

  return { fillRatio, edgeTouch }
}

/**
 * Classifies an object based on its metrics and a specified cutoff value.
 * The classification is determined by evaluating the fill ratio and edge touch metrics.
 *
 * @function
 * @param {Object} metrics - An object containing the metrics to evaluate.
 * @param {number} metrics.fillRatio - The fill ratio value, representing the proportion of space occupied.
 * @param {boolean} metrics.edgeTouch - A boolean indicating whether the object touches the edges.
 * @param {number} fillRatioCutoff - A numeric cutoff value for the fill ratio, used for classification.
 * @returns {string} - Returns "filled" if the object touches the edges or its fill ratio is greater than or equal to the cutoff; otherwise, returns "centered".
 */
const classifyByMetrics = ({ fillRatio, edgeTouch }, fillRatioCutoff) =>
  edgeTouch || fillRatio >= fillRatioCutoff ? 'filled' : 'centered'

/**
 * Asynchronously classifies an image based on its visual content and specified options.
 *
 * @param {HTMLImageElement} img - The image element to be classified.
 * @param {Object} [opts={}] - Optional settings to configure the classification process.
 * @param {number} [opts.maxSize=256] - Maximum dimension (width or height) for the downscaled image.
 * @param {number} [opts.diffThreshold=28] - Threshold for color difference to identify content boundaries.
 * @param {number} [opts.alphaThreshold=20] - Alpha transparency threshold to consider pixels for processing.
 * @param {number} [opts.edgeMargin=2] - Margin to consider when calculating edge-based metrics.
 * @param {number} [opts.fillRatioCutoff=0.55] - Cutoff ratio to classify image content based on fill metrics.
 * @returns {Promise<Object>} - A promise resolving to an object containing classification details:
 *                               - `type`: The classification type of the image content.
 *                               - `reason`: Reason for classification, applicable if no content is detected.
 *                               - `bbox`: Bounding box information of the detected content.
 *                               - `bg`: Averaged background color calculated from the image corners.
 *                               - Additional fill metrics calculated for classification.
 * @throws {Error} - Throws an error if the image classification process fails.
 */
const classifyImage = async (img, opts = {}) => {
  const settings = {
    maxSize: 256,
    diffThreshold: 28,
    alphaThreshold: 20,
    edgeMargin: 2,
    fillRatioCutoff: 0.55,
    ...opts
  }

  await waitForImage(img)
  
  const { w, h } = downscaleSize(img.naturalWidth, img.naturalHeight, settings.maxSize)
  const data = getImageData(img, w, h)

  const bg = avgCornerColor(data, w, h, settings.alphaThreshold)
  const bbox = findContentBoundingBox(
    data,
    w,
    h,
    bg,
    settings.diffThreshold,
    settings.alphaThreshold
  )

  if (!bbox) {
    return { type: 'unknown', reason: 'no-content-detected' }
  }

  const metrics = calcFillMetrics(bbox, w, h, settings.edgeMargin)
  const type = classifyByMetrics(metrics, settings.fillRatioCutoff)

  return { type, ...metrics, bbox, bg }
}

/**
 * Updates the class list and dataset properties of an image element
 * based on the provided result object.
 *
 * @param {HTMLElement} img - The image element to modify.
 * @param {Object} result - An object containing the properties used to determine
 *                          the classes and dataset attributes for the image element.
 * @param {string} result.type - A string indicating the type of the image.
 *                                Accepts values such as 'centered', 'filled', or others.
 * @param {number} [result.fillRatio] - A number representing the fill ratio of the image.
 *                                      This value is rounded to 3 decimal places if provided.
 * @param {boolean} [result.edgeTouch] - A boolean indicating whether the image touches the edge.
 *                                       If true, the dataset value will be set to '1'; otherwise, '0'.
 */
const applyClasses = (img, result) => {
  img.classList.remove('is-centered', 'is-filled', 'is-unknown')

  if (result.type === 'centered') img.classList.add('is-centered')
  else if (result.type === 'filled') img.classList.add('is-filled')
  else img.classList.add('is-unknown')

  img.dataset.imageType = result.type
  img.dataset.fillRatio = result.fillRatio?.toFixed?.(3) ?? ''
  img.dataset.edgeTouch = result.edgeTouch ? '1' : '0'
}

/**
 * Asynchronously classifies all product images on the page and applies classification results to each image.
 *
 * This function scans the DOM for elements with the class `product-image`, processes each image using
 * the `classifyImage` function, and applies the classification results via the `applyClasses` function.
 * In case of an error during classification, the image will be tagged with an "unknown" type and a
 * reason of "error."
 *
 * @param {Object} [opts={}] - Optional parameters to customize the behavior of the `classifyImage` function.
 *                             These options are passed directly to the classification function.
 * @returns {Promise<void>} A promise that resolves when all images have been classified and processed.
 */
const classifyAllProductImages = async (opts = {}) => {
  const images = [...document.querySelectorAll('.card-product img'), ...document.querySelectorAll('.productView-image .media img')]
  const observer = new IntersectionObserver(async (entries, obs) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue

      const img = entry.target
      obs.unobserve(img)

      try {
        await waitForImage(img)
        const result = await classifyImage(img)
        applyClasses(img, result)
      } catch (e) {
        console.warn('error processing', img, e)
      }
    }
  }, {
    rootMargin: '200px'
  })

  const mutationObserver = new MutationObserver(mutations => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach(node => {
        if (node.nodeType === 1 && node.matches('.lazyloaded')) {
          observer.observe(node)
        }

        if (node.querySelectorAll) {
          const imgs = [...node.querySelectorAll('.card-product img'), ...node.querySelectorAll('.productView-image .media img')]
          imgs.forEach(img => {
            observer.observe(img)
          })
        }
      })
    })
  })

  mutationObserver.observe(document.body, { childList: true, subtree: true })
  images.forEach(img => {
    observer.observe(img)
  })
}
document.addEventListener('DOMContentLoaded', async () => {
  await classifyAllProductImages()
});